/**
 * <ec-announcement-carousel>
 *
 * Mobile-only rotation of reassurance items. Activates below 641px and
 * pauses on tab visibility loss + when reduced motion is requested.
 */

const MOBILE_QUERY = '(max-width: 640px)';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

class EcAnnouncementCarousel extends HTMLElement {
  static observedAttributes = ['data-rotation-ms'];

  #intervalId = null;
  #activeIndex = 0;
  #mobileMql = null;
  #reducedMql = null;
  #onMobileChange = null;
  #onReducedChange = null;
  #onVisibility = null;

  connectedCallback() {
    this.#mobileMql = window.matchMedia(MOBILE_QUERY);
    this.#reducedMql = window.matchMedia(REDUCED_MOTION_QUERY);
    this.#onMobileChange = () => this.#evaluate();
    this.#onReducedChange = () => this.#evaluate();
    this.#onVisibility = () => {
      if (document.hidden) this.pause();
      else this.#evaluate();
    };

    this.#mobileMql.addEventListener('change', this.#onMobileChange);
    this.#reducedMql.addEventListener('change', this.#onReducedChange);
    document.addEventListener('visibilitychange', this.#onVisibility);

    this.#syncInitialActive();
    this.#evaluate();
  }

  disconnectedCallback() {
    this.pause();
    this.#mobileMql?.removeEventListener('change', this.#onMobileChange);
    this.#reducedMql?.removeEventListener('change', this.#onReducedChange);
    document.removeEventListener('visibilitychange', this.#onVisibility);
  }

  attributeChangedCallback(name) {
    if (name === 'data-rotation-ms' && this.isConnected) this.#evaluate();
  }

  pause() {
    if (this.#intervalId !== null) {
      clearInterval(this.#intervalId);
      this.#intervalId = null;
    }
  }

  resume() {
    this.#evaluate();
  }

  #evaluate() {
    this.pause();
    if (this.dataset.disabled === 'true') {
      this.#showAll();
      this.classList.remove('is-animating');
      return;
    }
    if (!this.#mobileMql?.matches) {
      // Desktop layout: all items visible via CSS, nothing to rotate.
      this.#showAll();
      this.classList.remove('is-animating');
      return;
    }
    if (this.#reducedMql?.matches) {
      this.#showAll();
      this.classList.remove('is-animating');
      return;
    }
    const items = this.#items();
    if (items.length < 2) {
      this.#showAll();
      this.classList.remove('is-animating');
      return;
    }

    // Sync state to a single active item *without* transitions, then
    // re-enable them so future advance() calls crossfade smoothly.
    this.classList.remove('is-animating');
    this.#syncInitialActive();
    // Force a reflow so the class removal commits before re-enabling.
    void this.offsetHeight;
    this.classList.add('is-animating');

    const ms = Math.max(2000, Number(this.dataset.rotationMs) || 3500);
    this.#intervalId = setInterval(() => this.#advance(), ms);
  }

  #items() {
    return /** @type {HTMLElement[]} */ (
      Array.from(this.querySelectorAll('.ec-announcement-bar__item'))
    );
  }

  #syncInitialActive() {
    const items = this.#items();
    items.forEach((el, idx) => {
      el.classList.toggle('is-active', idx === 0);
    });
    this.#activeIndex = 0;
  }

  #showAll() {
    this.#items().forEach((el) => el.classList.add('is-active'));
  }

  #advance() {
    const items = this.#items();
    if (items.length === 0) return;
    items[this.#activeIndex]?.classList.remove('is-active');
    this.#activeIndex = (this.#activeIndex + 1) % items.length;
    items[this.#activeIndex]?.classList.add('is-active');
  }
}

if (!customElements.get('ec-announcement-carousel')) {
  customElements.define('ec-announcement-carousel', EcAnnouncementCarousel);
}
